/**
 * Super-admin Marketplace Control page.
 *
 * Three sections:
 *   1. Global — enable/disable marketplace platform-wide.
 *   2. Location controls — blocklist rows keyed by country/state/city/pincode.
 *   3. Laboratory controls — per-lab override table. Super admin > lab's own choice.
 */
import React, { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/Layout";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Loader2, Save, Plus, Trash2, Globe, MapPin, Building2, ShieldCheck, ChevronRight } from "lucide-react";

const MODE_STYLES = {
  public: { label: "Public", cls: "bg-efgh-green/10 text-efgh-green" },
  hidden: { label: "Hidden", cls: "bg-efgh-orange/10 text-efgh-orange" },
  private: { label: "Private", cls: "bg-danger/10 text-danger" },
};

export default function AdminMarketplaceControl() {
  const [mc, setMc] = useState(null);          // marketplace_control config
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [matrix, setMatrix] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/labs/visibility-matrix");
      setMc(r.data.marketplace_control);
      setMatrix(r.data.labs || []);
      setDirty(false);
    } catch (err) { toast.error(apiError(err, "Could not load")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveMc = async () => {
    if (!mc) return;
    setSaving(true);
    try {
      const r = await api.put("/admin/marketplace-control", mc);
      setMc(r.data);
      setDirty(false);
      toast.success("Marketplace control saved");
      // Reload lab matrix so effective visibility reflects the new config
      const m = await api.get("/admin/labs/visibility-matrix");
      setMatrix(m.data.labs || []);
    } catch (err) { toast.error(apiError(err, "Save failed")); }
    finally { setSaving(false); }
  };

  const setLabOverride = async (labId, mode) => {
    try {
      await api.put(`/admin/labs/${labId}/visibility-override`, { mode });
      toast.success(mode ? `Override set: ${mode.toUpperCase()}` : "Override cleared — lab's own choice restored");
      const m = await api.get("/admin/labs/visibility-matrix");
      setMatrix(m.data.labs || []);
    } catch (err) { toast.error(apiError(err, "Override failed")); }
  };

  if (loading || !mc) {
    return (
      <DashboardLayout role="super_admin">
        <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-ink/40 mx-auto" /></div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="super_admin">
      <div className="max-w-5xl">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading font-bold text-3xl text-ink flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-efgh-orange" /> Marketplace Control
            </h1>
            <p className="text-ink/60 text-sm mt-1">Global toggle · location rules · per-lab overrides. Super Admin always wins over lab-level settings.</p>
          </div>
          <Button onClick={saveMc} disabled={!dirty || saving} data-testid="save-mc" className="bg-ink text-white hover:bg-ink/90 h-11">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save changes
          </Button>
        </div>

        {/* Priority explainer */}
        <Card className="mt-6 p-4 rounded-2xl border-l-4 border-efgh-orange bg-efgh-orange/5">
          <div className="flex items-center gap-2 text-sm text-ink">
            <div className="text-[10px] uppercase tracking-widest text-efgh-orange font-heading font-bold">Priority</div>
            <span className="text-ink/70 flex items-center gap-1.5">
              Global kill-switch <ChevronRight className="w-3 h-3" /> Location rules <ChevronRight className="w-3 h-3" /> Lab override <ChevronRight className="w-3 h-3" /> Lab&apos;s own choice
            </span>
          </div>
        </Card>

        {/* ===== 1. Global toggle ===== */}
        <Card className="mt-6 p-6 rounded-2xl" data-testid="mc-global">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-lg bg-efgh-orange/10 text-efgh-orange grid place-items-center"><Globe className="w-4 h-4" /></div>
            <div className="font-heading font-semibold text-lg text-ink">Global control</div>
          </div>
          <p className="text-sm text-ink/60">One switch to disable the entire EFGH marketplace across every lab.</p>
          <div className="mt-4 flex items-center gap-3">
            <Switch data-testid="mc-enabled" checked={!!mc.enabled} onCheckedChange={(v) => { setMc((p) => ({ ...p, enabled: v })); setDirty(true); }} />
            <span className={`text-sm font-semibold ${mc.enabled ? "text-efgh-green" : "text-danger"}`}>
              {mc.enabled ? "Marketplace ENABLED globally" : "Marketplace DISABLED globally — all labs treated as PRIVATE"}
            </span>
          </div>
        </Card>

        {/* ===== 2. Location controls ===== */}
        <Card className="mt-6 p-6 rounded-2xl" data-testid="mc-location">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-lg bg-efgh-blue/10 text-efgh-blue grid place-items-center"><MapPin className="w-4 h-4" /></div>
                <div className="font-heading font-semibold text-lg text-ink">Location controls</div>
              </div>
              <p className="text-sm text-ink/60">Any lab matching one of these rules is forced to <b>Hidden</b> (dropped from search &amp; marketplace, direct URLs keep working). Leave a field blank to match all values of that field.</p>
            </div>
            <Button size="sm" variant="outline" data-testid="mc-add-rule" onClick={() => { setMc((p) => ({ ...p, location_blocklist: [...(p.location_blocklist || []), { country: "IN" }] })); setDirty(true); }}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Add rule
            </Button>
          </div>
          <div className="mt-4 space-y-2">
            {(mc.location_blocklist || []).length === 0 && (
              <div className="p-4 border border-dashed border-border rounded-lg text-center text-sm text-ink/50">No location rules. All labs are eligible based on their own choice.</div>
            )}
            {(mc.location_blocklist || []).map((rule, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 items-end" data-testid={`mc-rule-${i}`}>
                <FieldSm label="Country" value={rule.country || ""} onChange={(v) => { const arr = [...mc.location_blocklist]; arr[i] = { ...arr[i], country: v || undefined }; setMc({ ...mc, location_blocklist: arr }); setDirty(true); }} testid={`mc-rule-${i}-country`} placeholder="IN" />
                <FieldSm label="State" value={rule.state || ""} onChange={(v) => { const arr = [...mc.location_blocklist]; arr[i] = { ...arr[i], state: v || undefined }; setMc({ ...mc, location_blocklist: arr }); setDirty(true); }} testid={`mc-rule-${i}-state`} placeholder="KA" />
                <FieldSm label="City" value={rule.city || ""} onChange={(v) => { const arr = [...mc.location_blocklist]; arr[i] = { ...arr[i], city: v || undefined }; setMc({ ...mc, location_blocklist: arr }); setDirty(true); }} testid={`mc-rule-${i}-city`} placeholder="Bengaluru" />
                <FieldSm label="Pincode" value={rule.pincode || ""} onChange={(v) => { const arr = [...mc.location_blocklist]; arr[i] = { ...arr[i], pincode: v || undefined }; setMc({ ...mc, location_blocklist: arr }); setDirty(true); }} testid={`mc-rule-${i}-pincode`} placeholder="560001" />
                <button type="button" onClick={() => { const arr = [...mc.location_blocklist]; arr.splice(i, 1); setMc({ ...mc, location_blocklist: arr }); setDirty(true); }} data-testid={`mc-rule-${i}-remove`} className="h-9 w-9 grid place-items-center text-ink/40 hover:text-danger">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </Card>

        {/* ===== 3. Laboratory controls ===== */}
        <Card className="mt-6 p-6 rounded-2xl" data-testid="mc-labs">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-lg bg-efgh-green/10 text-efgh-green grid place-items-center"><Building2 className="w-4 h-4" /></div>
            <div className="font-heading font-semibold text-lg text-ink">Laboratory controls</div>
          </div>
          <p className="text-sm text-ink/60">Override visibility for any specific lab. This wins over the lab&apos;s own choice.</p>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm" data-testid="mc-lab-table">
              <thead className="text-[10px] uppercase tracking-widest text-ink/50 font-heading">
                <tr>
                  <th className="text-left px-2 py-2">Laboratory</th>
                  <th className="text-left px-2 py-2">Location</th>
                  <th className="text-left px-2 py-2">Lab&apos;s choice</th>
                  <th className="text-left px-2 py-2">Admin override</th>
                  <th className="text-left px-2 py-2">Effective</th>
                </tr>
              </thead>
              <tbody>
                {matrix.map((row) => {
                  const eff = MODE_STYLES[row.effective] || MODE_STYLES.public;
                  const own = MODE_STYLES[row.lab_choice] || MODE_STYLES.public;
                  return (
                    <tr key={row.id} className="border-t border-border/60 hover:bg-muted/30" data-testid={`mc-lab-row-${row.id}`}>
                      <td className="px-2 py-3">
                        <div className="font-semibold text-ink">{row.name}</div>
                        <div className="text-[11px] text-ink/50 font-mono">{row.slug}</div>
                      </td>
                      <td className="px-2 py-3 text-ink/70 text-xs">
                        {[row.city, row.state, row.pincode].filter(Boolean).join(" · ") || "—"}
                      </td>
                      <td className="px-2 py-3">
                        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${own.cls}`}>{own.label}</span>
                      </td>
                      <td className="px-2 py-3">
                        <Select value={row.admin_override || "auto"} onValueChange={(v) => setLabOverride(row.id, v === "auto" ? null : v)}>
                          <SelectTrigger className="w-[130px] h-8 text-xs" data-testid={`mc-override-${row.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="auto">Auto (no override)</SelectItem>
                            <SelectItem value="public">Force Public</SelectItem>
                            <SelectItem value="hidden">Force Hidden</SelectItem>
                            <SelectItem value="private">Force Private</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-2 py-3">
                        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded font-bold ${eff.cls}`}>{eff.label}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
}

function FieldSm({ label, value, onChange, testid, placeholder }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-ink/50">{label}</Label>
      <Input data-testid={testid} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className="h-9 text-sm" />
    </div>
  );
}
